import React from "react";
import { AlertTriangle, Clock, PauseCircle, CheckCircle2 } from "lucide-react";
import {
  computeTicketSla,
  formatCountdown,
  formatCountdownMagnitude,
  SLA_STATE,
  SLA_TONE,
  SLA_CLOCK_LABELS,
} from "utilities/sla";
import RelativeTime from "components/RelativeTime";

/**
 * SlaBadge (SOUND-534) — compact SLA indicator for the inbox column (and any
 * dense row). Surfaces the WORST active clock:
 *   breached → destructive chip ("overdue 40m")
 *   approaching → warning chip ("2h left")     (≥80% of target elapsed)
 *   paused → muted "Paused" (clock frozen — no running countdown)
 *   on-track → quiet muted countdown
 *   met/closed → muted "Met"
 * Colors are the semantic warning/destructive tokens, never the brand accent.
 *
 * Geometry comes from the shared `.badge` class rather than being hand-rolled,
 * so this chip is the same shape and height as the status pill sitting next to
 * it in the row. It previously used rounded-md + px-1.5 against .badge's
 * rounded-full + px-2, which read as misalignment in the inbox. The two
 * text-only variants (Met, on-track) carry .badge's padding without its border
 * so a mixed column keeps one baseline.
 * Renders nothing when the ticket has no SLA policy (caller shows "—").
 * Internal-only surface — gate at the call site.
 */
export default function SlaBadge({ ticket, now, className = "" }) {
  const sla = computeTicketSla(ticket, now);
  if (!sla.hasSla) return null;

  // Closed / fully satisfied — no active clock left.
  if (!sla.worst) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 align-middle text-xs font-medium text-success ${className}`}
        title="SLA met"
      >
        <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
        Met
      </span>
    );
  }

  const { state, clock, dueAt, remainingMs } = sla.worst;
  const clockLabel = SLA_CLOCK_LABELS[clock] || clock;
  const absTitle = (
    <>
      {clockLabel} due <RelativeTime value={dueAt} />
    </>
  );

  // Paused — show a frozen affordance, not a running countdown.
  if (sla.paused) {
    const tone = SLA_TONE.paused;
    return (
      <span
        className={`badge ${tone.chip} ${tone.chipBg} ${className}`}
        title={`SLA paused — ${clockLabel} clock frozen`}
      >
        <PauseCircle className="h-3 w-3" aria-hidden="true" />
        Paused
      </span>
    );
  }

  const countdown = formatCountdownMagnitude(remainingMs);
  // The words move into the tooltip — the icon carries breached vs running.
  const fullText = formatCountdown(remainingMs);

  // On-track — keep the column calm (muted text, no colored chip).
  if (state === SLA_STATE.OK) {
    const tone = SLA_TONE.ok;
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 align-middle text-xs font-medium ${tone.text} ${className}`}
        title={`${clockLabel} · on track · ${fullText}`}
      >
        <Clock className="h-3 w-3" aria-hidden="true" />
        {countdown}
      </span>
    );
  }

  const tone = SLA_TONE[state] || SLA_TONE.ok;
  const Icon = state === SLA_STATE.BREACHED ? AlertTriangle : Clock;
  return (
    <span
      className={`badge ${tone.chip} ${tone.chipBg} ${className}`}
      title={`${clockLabel} · ${state === SLA_STATE.BREACHED ? "breached" : "approaching"} · ${fullText}`}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      <span className="whitespace-nowrap">{countdown}</span>
      <span className="sr-only"> — {absTitle}</span>
    </span>
  );
}
